import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import * as safeService from '../services/safeService';

const DENOMS = {
  usd: [100, 50, 20, 10, 5, 1],
  srd: [500, 200, 100, 50, 20, 10, 5],
  eur: [100, 50, 20, 10, 5],
};
const SYMBOLS = { usd: '$', srd: 'SRD', eur: '€' };
const CURR = ['usd', 'srd', 'eur'];

function money(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}

/**
 * Safe bill counter: count physical bills per denomination per currency, compare
 * to the running safe saldo, show the difference. Saves the count for the day.
 */
export default function SafeCountModal({ date, saldo: saldoProp, t, onClose, onSaved }) {
  const [counts, setCounts] = useState({ usd: {}, srd: {}, eur: {} });
  const [saldo, setSaldo] = useState(saldoProp || { usd: 0, srd: 0, eur: 0 });
  const [hasSaved, setHasSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await safeService.getClosing(date);
      if (cancelled) return;
      if (res.success && res.data) {
        if (res.data.saldo) setSaldo(res.data.saldo);
        if (res.data.closing) {
          setHasSaved(true);
          const c = res.data.closing.counts || {};
          const norm = { usd: {}, srd: {}, eur: {} };
          for (const cur of CURR) {
            const map = c[cur] || {};
            for (const d of DENOMS[cur]) {
              const v = map[String(d)];
              if (v !== undefined && v !== null && Number(v) !== 0) norm[cur][d] = String(v);
            }
          }
          setCounts(norm);
        }
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [date]);

  const setCount = (cur, denom, val) => {
    setCounts((prev) => ({ ...prev, [cur]: { ...prev[cur], [denom]: val } }));
  };

  const counted = useMemo(() => {
    const out = { usd: 0, srd: 0, eur: 0 };
    for (const cur of CURR) {
      for (const d of DENOMS[cur]) {
        const n = parseInt(counts[cur][d], 10);
        if (Number.isFinite(n) && n > 0) out[cur] += d * n;
      }
    }
    return out;
  }, [counts]);

  const diff = {
    usd: counted.usd - (saldo.usd || 0),
    srd: counted.srd - (saldo.srd || 0),
    eur: counted.eur - (saldo.eur || 0),
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { usd: {}, srd: {}, eur: {} };
      for (const cur of CURR) {
        for (const d of DENOMS[cur]) {
          const n = parseInt(counts[cur][d], 10);
          if (Number.isFinite(n) && n > 0) payload[cur][d] = n;
        }
      }
      const res = await safeService.saveClosing(date, payload);
      if (res.success) {
        if (onSaved) onSaved(res.data);
        onClose();
      } else {
        alert(res.message || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal kassa-close-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="safeCountTitle">
        <div className="modal-header">
          <h3 id="safeCountTitle">{t('safe_count_title')}</h3>
          <div className="muted small">{date}{hasSaved ? ` • ${t('kassa_saved')}` : ''}</div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="muted">{t('loading')}</div>
          ) : (
            <div className="kassa-close-grid">
              {CURR.map((cur) => (
                <div className="kassa-close-col" key={cur}>
                  <div className="kassa-close-col-head">{SYMBOLS[cur]} {cur.toUpperCase()}</div>
                  <table className="kassa-count-table">
                    <thead>
                      <tr>
                        <th>{t('kassa_bill')}</th>
                        <th className="gp-num">{t('kassa_count')}</th>
                        <th className="gp-num">{t('total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DENOMS[cur].map((d) => {
                        const n = parseInt(counts[cur][d], 10);
                        const rowTotal = Number.isFinite(n) && n > 0 ? d * n : 0;
                        return (
                          <tr key={d}>
                            <td className="kassa-denom">{money(d)}</td>
                            <td className="gp-num">
                              <input
                                className="gp-cell" type="number" min="0" step="1" inputMode="numeric" placeholder="0"
                                value={counts[cur][d] ?? ''}
                                onChange={(e) => setCount(cur, d, e.target.value)}
                              />
                            </td>
                            <td className="gp-num kassa-rowtotal">{rowTotal ? money(rowTotal) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="gp-foot-label">{t('kassa_counted')}</td>
                        <td></td>
                        <td className="gp-num gp-foot-total">{money(counted[cur])}</td>
                      </tr>
                      <tr>
                        <td className="gp-foot-label">{t('safe_saldo')}</td>
                        <td></td>
                        <td className="gp-num">{money(saldo[cur] || 0)}</td>
                      </tr>
                      <tr className="kassa-diff-row">
                        <td className="gp-foot-label">{t('kassa_difference')}</td>
                        <td></td>
                        <td className={'gp-num kassa-diff ' + (Math.abs(diff[cur]) < 0.005 ? 'ok' : (diff[cur] > 0 ? 'over' : 'short'))}>
                          {diff[cur] > 0 ? '+' : (diff[cur] < 0 ? '−' : '')}{money(Math.abs(diff[cur]))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            <FiX style={{ marginRight: '6px' }} /> {t('cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            <FiSave style={{ marginRight: '6px' }} /> {saving ? t('saving') : t('safe_count_save')}
          </button>
        </div>
      </div>
    </div>
  );
}
